import {
  argbFromHex,
  hexFromArgb,
  themeFromSourceColor,
} from "@material/material-color-utilities";

/**
 * 整個產品的視覺基準色。**這是唯一需要改的地方。**
 *
 * 換成公司品牌色之後，亮色與暗色的完整 MD3 色彩系統會自動重算，
 * web 與 app 兩端同步更新。
 */
export const SEED_COLOR = "#0B57D0";

/**
 * MD3 的色彩角色。
 *
 * 命名完全依照 Material Design 3 規格，所以可以近乎 1:1 對應到
 * React Native Paper 的 theme.colors。
 */
export interface Md3ColorScheme {
  primary: string;
  onPrimary: string;
  primaryContainer: string;
  onPrimaryContainer: string;

  secondary: string;
  onSecondary: string;
  secondaryContainer: string;
  onSecondaryContainer: string;

  tertiary: string;
  onTertiary: string;
  tertiaryContainer: string;
  onTertiaryContainer: string;

  error: string;
  onError: string;
  errorContainer: string;
  onErrorContainer: string;

  background: string;
  onBackground: string;

  surface: string;
  onSurface: string;
  surfaceVariant: string;
  onSurfaceVariant: string;

  /** MD3 的表面層級。Paper 的 elevation.level0~5 與 MUI 的卡片背景都用它們 */
  surfaceDim: string;
  surfaceBright: string;
  surfaceContainerLowest: string;
  surfaceContainerLow: string;
  surfaceContainer: string;
  surfaceContainerHigh: string;
  surfaceContainerHighest: string;

  outline: string;
  outlineVariant: string;
  shadow: string;
  scrim: string;

  inverseSurface: string;
  inverseOnSurface: string;
  inversePrimary: string;
}

export interface Md3Palette {
  light: Md3ColorScheme;
  dark: Md3ColorScheme;
}

/**
 * MD3 規格中，表面層級是由 neutral 色調盤的固定色調值取出來的。
 * material-color-utilities 的 Scheme 沒有直接提供這幾個，所以依規格自己取。
 */
const SURFACE_TONES = {
  light: {
    surfaceDim: 87,
    surfaceBright: 98,
    surfaceContainerLowest: 100,
    surfaceContainerLow: 96,
    surfaceContainer: 94,
    surfaceContainerHigh: 92,
    surfaceContainerHighest: 90,
  },
  dark: {
    surfaceDim: 6,
    surfaceBright: 24,
    surfaceContainerLowest: 4,
    surfaceContainerLow: 10,
    surfaceContainer: 12,
    surfaceContainerHigh: 17,
    surfaceContainerHighest: 22,
  },
} as const;

/** 從一顆 seed color 產生亮色與暗色兩套完整的 MD3 色彩系統。 */
export function createMd3Palette(seedColor: string = SEED_COLOR): Md3Palette {
  const generated = themeFromSourceColor(argbFromHex(seedColor));
  const neutral = generated.palettes.neutral;

  const build = (mode: "light" | "dark"): Md3ColorScheme => {
    const scheme = generated.schemes[mode];
    const tones = SURFACE_TONES[mode];
    const hex = (argb: number) => hexFromArgb(argb);
    const tone = (value: number) => hexFromArgb(neutral.tone(value));

    return {
      primary: hex(scheme.primary),
      onPrimary: hex(scheme.onPrimary),
      primaryContainer: hex(scheme.primaryContainer),
      onPrimaryContainer: hex(scheme.onPrimaryContainer),

      secondary: hex(scheme.secondary),
      onSecondary: hex(scheme.onSecondary),
      secondaryContainer: hex(scheme.secondaryContainer),
      onSecondaryContainer: hex(scheme.onSecondaryContainer),

      tertiary: hex(scheme.tertiary),
      onTertiary: hex(scheme.onTertiary),
      tertiaryContainer: hex(scheme.tertiaryContainer),
      onTertiaryContainer: hex(scheme.onTertiaryContainer),

      error: hex(scheme.error),
      onError: hex(scheme.onError),
      errorContainer: hex(scheme.errorContainer),
      onErrorContainer: hex(scheme.onErrorContainer),

      background: hex(scheme.background),
      onBackground: hex(scheme.onBackground),

      surface: hex(scheme.surface),
      onSurface: hex(scheme.onSurface),
      surfaceVariant: hex(scheme.surfaceVariant),
      onSurfaceVariant: hex(scheme.onSurfaceVariant),

      surfaceDim: tone(tones.surfaceDim),
      surfaceBright: tone(tones.surfaceBright),
      surfaceContainerLowest: tone(tones.surfaceContainerLowest),
      surfaceContainerLow: tone(tones.surfaceContainerLow),
      surfaceContainer: tone(tones.surfaceContainer),
      surfaceContainerHigh: tone(tones.surfaceContainerHigh),
      surfaceContainerHighest: tone(tones.surfaceContainerHighest),

      outline: hex(scheme.outline),
      outlineVariant: hex(scheme.outlineVariant),
      shadow: hex(scheme.shadow),
      scrim: hex(scheme.scrim),

      inverseSurface: hex(scheme.inverseSurface),
      inverseOnSurface: hex(scheme.inverseOnSurface),
      inversePrimary: hex(scheme.inversePrimary),
    };
  };

  return { light: build("light"), dark: build("dark") };
}

/** 預設色盤。多數情況直接用這個就好。 */
export const md3Palette: Md3Palette = createMd3Palette();
